import {Component, Inject, OnInit, Optional, SkipSelf, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {action, computed, observable} from 'mobx-angular';
import {
  AppComponent as LfAppComponent,
  AtActions,
  LfGlobalValidationIssues,
  LfPrint,
  ModalComponent,
  SubmitComponent
} from 'lf-at-theme';
import {LfFileStorage, LfI18n, LfStorage, LfUnloadAlert} from '@lightweightform/core';

import {RestService} from './services/rest.service';

import {convertDatesUnionJs2Json, convertDatesUnionJson2Js} from './union-form/union-form.schema';
import {FIXED_VALUES_OBS, INITIAL_VALUES, UnionInitialValues} from './tokens';
import {SubmitResponseModel} from './entities/SubmitResponseModel';
import {markAsWarnings} from '../common/issues';
import {makeObservable} from "mobx";
import { format } from 'date-fns';

@Component({
  selector: 'union-form-root',
  templateUrl: './union.component.html',
  styleUrls: ['./union.component.scss'],
  providers: [
    // Provide InitialValues to all child components in a component-scoped DI context.
    {
      provide: INITIAL_VALUES,
      useFactory: (comp: UnionComponent) => comp.initialValues,
      deps: [UnionComponent]
    }
  ]
})
export class UnionComponent implements OnInit {

  @computed
  get actions(): AtActions[] {
    const isDebug = false;
    const isForSubmit = this.initialValues.mode.new || this.initialValues.mode.edit;

    const formActions: {before: AtActions[], after: AtActions[]} = {
      before: [
        {
          id: 'save',
          label: 'Save',
          icon: 'save',
          isVertical: true,
          callback: () => this._lfFileStorage
            .saveToFile('/',
              `${this._lfI18n.get('saveFileName')}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`)
            .then(() => console.log('Value saved successfully'))
            .catch(err => console.error('Error saving file: ', err)),
        },
        {
          id: 'load',
          label: 'Load',
          faIcon: 'upload',
          isVertical: true,
          callback: () => this._lfFileStorage
            .loadFromFile('/')
            .then((loaded) => {
              if (loaded) {
                console.log('Value loaded successfully');
              }
            })
            .catch(err => {
              err.i18nErrorKey = 'loadError';
              throw err;
            }),
        },
        {
          id: 'validate',
          label: 'Validate',
          icon: 'validate',
          isVertical: true,
          callback: () => this._lfApp.validate(),
        },
      ],
      after: [
        {
          id: 'submit',
          label: 'Submit',
          style: 'success oss-submit',
          icon: 'next',
          iconStyle: 'pull-right',
          isDisabled: this.isSubmitting,
          callback: () =>
            this._lfSubmit.submit((model) => this.onSubmit(model)),
        },
      ]
    };

    const langActions: AtActions[] = this._lfI18n.languages.map(lang => ({
      id: lang,
      label: lang,
      faIcon: 'language',
      isVertical: true,
      isDisabled: () => lang == this._lfI18n.currentLanguage,
      callback: async () => {
        await this._lfI18n.setCurrentLanguage(lang)
      }
    }));

    const debugActions: AtActions[] = [
      {
        id: 'schema',
        label: 'Schema',
        faIcon: 'bug',
        style: 'danger',
        isVertical: true,
        callback: () => {
          this._schemaModal.show();
        },
      },
      {
        id: 'value',
        label: 'Value',
        faIcon: 'bug',
        style: 'danger',
        isVertical: true,
        callback: () => {
          this._valueModal.show();
        },
      },
      {
        id: 'initialValues',
        label: 'IniVals',
        faIcon: 'bug',
        style: 'danger',
        isVertical: true,
        callback: () => {
          this._initValsModal.show();
        },
      },
      {
        id: 'error',
        label: 'Error',
        faIcon: 'bug',
        style: 'danger',
        isVertical: true,
        callback: () => {
          throw new Error('Erro');
        },
      },
    ];

    return [
      ...isForSubmit ? formActions.before : [],
      //TODO Re-enable when we add print templates
      //{
      //  id: 'print',
      //  label: 'Print',
      //  icon: 'print',
      //  isVertical: true,
      //  callback: () => {
      //    this._lfPrint.print();
      //  },
      //},
      ...isDebug ? debugActions : [],

      ...langActions,

      ...isForSubmit ? formActions.after : [],
    ];
  }

  @ViewChild(LfAppComponent) _lfApp: LfAppComponent;

  @ViewChild(SubmitComponent) _lfSubmit: SubmitComponent;

  @ViewChild('valueModal') _valueModal: ModalComponent;

  @ViewChild('schemaModal') _schemaModal: ModalComponent;

  @ViewChild('initialValuesModal') _initValsModal: ModalComponent;

  public initialValues: UnionInitialValues;
  @observable public isSubmitting: boolean = false;


  constructor(private _lfPrint: LfPrint,
              public _lfStorage: LfStorage,
              public _lfFileStorage: LfFileStorage,
              public _lfI18n: LfI18n,
              private _lfGlobalValidationIssues: LfGlobalValidationIssues,
              private lfUnloadAlert: LfUnloadAlert,
              private restService: RestService,
              @Optional() @SkipSelf() @Inject(FIXED_VALUES_OBS) private fixedValues$: Observable<UnionInitialValues | any>
  ) {
    makeObservable(this);
  }

  ngOnInit(): void {
    console.debug('ngOnInit');
    if (this.fixedValues$) {
      console.debug('ngOnInit: got fixedValues observable');
      this.processUndefInitialValues();
      this.fixedValues$.subscribe({
        next: (initVals) => {
          console.debug('ngOnInit: new observ val', initVals);
          if (initVals) {
            this.processInitialValues(initVals);
          } else {
            this.processUndefInitialValues();
          }
        }
      });

    } else {
      console.debug('ngOnInit: no fixedValues observable');
      this.processUndefInitialValues();

    }
  }

  @action
  processUndefInitialValues() {
    console.debug('processUndefInitialValues: did reset');
    this.initialValues = {
      mode: {detail: true, new: false, edit: false},
      hasCadastroData: true,
      isNonResident: false,
      loadedPrevRegistration: true,
      isPreRegistration: false,
      isSupplyCommencementEditable: false,
      preFilledForm: undefined
    };
    this._lfStorage.setStateProperty('/', 'initialValues', this.initialValues);
    this._lfStorage.reset();

    this.updateUnloadAlert(null);
  }

  @action
  processInitialValues(initialValues) {
    console.debug('processInitialValues: did process initVals');
    this.initialValues = initialValues;
    convertDatesUnionJson2Js(initialValues.preFilledForm ?? {});
    this._lfStorage.setStateProperty('/', 'initialValues', initialValues);
    if (initialValues.preFilledForm) {
      // Note: This only works if every non-nullable field is filled.
      this._lfStorage.set('/union-form', initialValues.preFilledForm);
    }
    this.updateUnloadAlert(initialValues);
  }

  updateUnloadAlert(initialValues) {
    if (initialValues?.mode?.detail) {
      this.lfUnloadAlert.disable();
    } else {
      this.lfUnloadAlert.enable();
    }
  }

  @action
  public onSubmit(model) {
    // send union-form to the server
    const unionForm = this._lfStorage.getAsJS('./union-form');
    convertDatesUnionJs2Json(unionForm);
    let submitObservable: Observable<SubmitResponseModel>;
    if (this.initialValues.mode.new) submitObservable = this.restService.submitNew(unionForm);
    if (this.initialValues.mode.edit) submitObservable = this.restService.submitEdit(unionForm);
    this.isSubmitting = true;
    submitObservable
      .pipe(finalize(() => {this.isSubmitting = false;}))
      .subscribe({
      // Show generic error modal if we get an error
      // Show validation errors modal if the server returns validation errors
      // Redirect to success page if all was successful
      next: (response) => {
        if (response.success) {
          this.lfUnloadAlert.disable();
          window.location.href = response.redirectUrl;

        } else {
          console.info('Received server validation errors. ServerResponse: ', response);
          markAsWarnings(response.validationIssuesMap);
          this._lfGlobalValidationIssues.setGlobalIssues(response.validationIssuesMap);
          this._lfApp.validate();// Try to re-validate and show validations panel
        }
      }
    });
  }

  redirect(s: string) {
    window.location.replace(s);
  }
}
